import { useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { MainLayout } from '@/components/layout/MainLayout';
import { PageHeader } from '@/components/layout/PageHeader';
import { useAuthContext } from '@/contexts/AuthContext';
import { usePlanLimits } from '@/hooks/useOrganization';
import { useStripePortal, useCheckoutPolling } from '@/hooks/useStripeBilling';
import { useAnalytics } from '@/hooks/useAnalytics';
import { PlanCards } from '@/components/billing/PlanCards';
import { Loader2, ExternalLink, AlertTriangle } from 'lucide-react';

interface UsageRowProps {
  tipo: string;
  labelKey: string;
}

function UsageRow({ tipo, labelKey }: UsageRowProps) {
  const { t } = useTranslation();
  const { data, isLoading } = usePlanLimits(tipo);
  const label = t(labelKey);

  if (isLoading) {
    return (
      <tr>
        <td className="font-medium text-sm">{label}</td>
        <td>
          <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
        </td>
        <td />
      </tr>
    );
  }

  const usoAtual = data?.uso_atual ?? 0;
  const limite = data?.limite ?? null;
  const percentage =
    limite !== null && limite > 0 ? Math.min((usoAtual / limite) * 100, 100) : 0;
  const isWarning = limite !== null && percentage >= 80;
  const limiteLabel = limite === null ? t('common.unlimited') : String(limite);

  return (
    <tr>
      <td className="font-medium text-sm">
        <span className="inline-flex items-center gap-1.5">
          {label}
          {isWarning && (
            <AlertTriangle
              className="h-3.5 w-3.5 text-foreground"
              aria-label={t('billing.usageNearLimit', 'Próximo do limite')}
            />
          )}
        </span>
      </td>
      <td className="mono" style={{ fontSize: 12 }}>
        {usoAtual} / {limiteLabel}
      </td>
      <td style={{ width: 180 }}>
        {limite !== null ? (
          <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
            <div
              className="h-full bg-foreground rounded-full transition-[width] duration-300"
              style={{ width: `${percentage}%` }}
              aria-label={`${percentage.toFixed(0)}%`}
            />
          </div>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
    </tr>
  );
}

const RESOURCES = [
  { tipo: 'prestadores', labelKey: 'billing.resources.activeProviders' },
  { tipo: 'admins', labelKey: 'billing.resources.adminAgents' },
  { tipo: 'clientes', labelKey: 'billing.resources.crmClients' },
  { tipo: 'os_mes', labelKey: 'billing.resources.osPerMonth' },
];

export default function ConfiguracoesPlano() {
  const { t } = useTranslation();
  const { organization } = useAuthContext();
  const [searchParams, setSearchParams] = useSearchParams();
  const portalMutation = useStripePortal();
  const { isPolling, isTimeout, startPolling } = useCheckoutPolling();
  const { track } = useAnalytics();

  useEffect(() => {
    track('upgrade_cta_viewed');
  }, [track]);

  const plano = organization?.plano ?? 'starter';
  const planoLabel = t(`billing.plans.${plano}` as const) ?? plano;
  const planoPreco = t(`billing.prices.${plano}` as const) ?? '—';
  const hasSubscription = plano !== 'starter' && plano !== 'enterprise';
  const isStarter = plano === 'starter';
  const planPillTone = isStarter ? '' : 'status-pill--ink';

  useEffect(() => {
    if (searchParams.get('checkout') === 'success') {
      startPolling();
      setSearchParams({}, { replace: true });
    }
  }, [searchParams, startPolling, setSearchParams]);

  return (
    <MainLayout>
      <div className="p-6 max-w-4xl mx-auto flex flex-col gap-5">
        <PageHeader titulo={t('billing.title')} />

        {/* Estados de checkout — feedback transitório, monocromático */}
        {isPolling && (
          <div className="ow-card">
            <div className="p-4 flex items-center gap-3">
              <Loader2 className="h-5 w-5 animate-spin text-foreground" />
              <div>
                <p className="font-medium text-sm">{t('billing.processingPayment')}</p>
                <p className="text-xs text-muted-foreground">{t('billing.planWillUpdate')}</p>
              </div>
            </div>
          </div>
        )}

        {isTimeout && (
          <div className="ow-card">
            <div className="p-4 text-sm text-muted-foreground">
              {t('billing.planUpdateTimeout')}
            </div>
          </div>
        )}

        {/* Plano atual */}
        <div className="ow-card">
          <div className="p-6">
            <div className="flex items-center justify-between gap-3 mb-3">
              <span className="text-[11px] uppercase tracking-wider font-semibold text-muted-foreground">
                {t('billing.planLabel')}
              </span>
              <span className={`status-pill ${planPillTone}`}>{planoLabel}</span>
            </div>
            <p className="text-3xl font-bold tabular-nums leading-none">{planoPreco}</p>
          </div>
        </div>

        {/* Uso de recursos */}
        <div className="ow-card">
          <div className="px-6 pt-5 pb-3">
            <h3 className="text-base font-bold tracking-tight">{t('billing.usage')}</h3>
          </div>
          <div className="px-2 pb-2">
            <table className="tbl">
              <tbody>
                {RESOURCES.map((r) => (
                  <UsageRow key={r.tipo} tipo={r.tipo} labelKey={r.labelKey} />
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Gerir subscrição (planos pagos) */}
        {hasSubscription && (
          <div className="ow-card">
            <div className="p-5 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <p className="font-medium text-sm">{t('billing.manageSubscription')}</p>
              <button
                type="button"
                className="ow-btn ow-btn--outline ow-btn--sm"
                onClick={() => portalMutation.mutate()}
                disabled={portalMutation.isPending}
              >
                {portalMutation.isPending ? (
                  <Loader2 size={14} className="animate-spin" />
                ) : (
                  <ExternalLink size={14} />
                )}
                {t('billing.manageSubscription')}
              </button>
            </div>
          </div>
        )}

        {/* Upgrade (apenas Starter) */}
        {isStarter && (
          <div className="ow-card">
            <div className="px-6 pt-5 pb-3">
              <h3 className="text-base font-bold tracking-tight">
                {t('billing.upgradeSection')}
              </h3>
            </div>
            <div className="px-6 pb-6">
              <PlanCards />
            </div>
          </div>
        )}
      </div>
    </MainLayout>
  );
}
